import { CockpitAreaGate } from "@/components/cockpit/CockpitAreaGate";
import { EmptyAreaNote } from "@/components/cockpit/EmptyAreaNote";
import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Eye, EyeOff, KeyRound, Loader2, ShieldCheck, MailQuestion, X } from "lucide-react";
import { CockpitPageShell } from "@/components/cockpit/CockpitPageShell";
import { CockpitSkeletonBlock } from "@/components/cockpit/CockpitSkeleton";
import { toast } from "sonner";
import {
  createCredentialHandover,
  listMyCredentialHandovers,
} from "@/lib/credential-handovers.functions";
import { listMyCredentialHandoverRequests } from "@/lib/credential-handover-requests.functions";
import { refreshCockpitSession } from "@/lib/cockpit-session-cache";
import {
  CREDENTIAL_CATEGORIES,
  CREDENTIAL_CATEGORY_LABELS,
  CREDENTIAL_LABEL_REQUIRED,
  credentialDisplayName,
  credentialLabelHint,
  credentialStatusLabel,
  type CredentialCategory,
} from "@/lib/credential-handovers.shared";

export const Route = createFileRoute("/_cockpit/cockpit/passwoerter")({

  head: () => ({
    meta: [
      { title: "Zugangsdaten – Cockpit – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: () => (
    <CockpitAreaGate area="credentials">
      <CockpitPasswordsPage />
    </CockpitAreaGate>
  ),
});


const dateFmt = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

function CockpitPasswordsPage() {
  const listFn = useServerFn(listMyCredentialHandovers);
  const createFn = useServerFn(createCredentialHandover);
  const listRequestsFn = useServerFn(listMyCredentialHandoverRequests);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Direktlink-Parameter ohne Routen-Schema lesen (nur clientseitig relevant).
  const requestedId =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("anforderung")
      : null;

  const formRef = useRef<HTMLFormElement | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["cockpit", "credential-handovers"],
    queryFn: () => listFn(),
    staleTime: 30_000,
  });

  const requestsQuery = useQuery({
    queryKey: ["cockpit", "credential-requests"],
    queryFn: () => listRequestsFn(),
    staleTime: 30_000,
  });
  const openRequests = requestsQuery.data?.rows ?? [];

  const denied = data ? data.allowed === false : false;
  useEffect(() => {
    if (denied) navigate({ to: "/cockpit", replace: true });
  }, [denied, navigate]);

  const [category, setCategory] = useState<CredentialCategory>("facebook");
  const [categoryLabel, setCategoryLabel] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [note, setNote] = useState("");
  const [activeRequestId, setActiveRequestId] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const labelRequired = CREDENTIAL_LABEL_REQUIRED.includes(category);

  function selectRequest(req: {
    id: string;
    category: CredentialCategory;
    categoryLabel: string | null;
  }) {
    setActiveRequestId(req.id);
    setCategory(req.category);
    setCategoryLabel(req.categoryLabel ?? "");
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  // Direktlink aus Mail bzw. Onboarding-Checkliste: Kategorie vorauswählen.
  const preselected = useRef(false);
  useEffect(() => {
    if (preselected.current || !requestedId) return;
    const req = openRequests.find((r) => r.id === requestedId);
    if (req) {
      preselected.current = true;
      selectRequest(req);
    }
  }, [requestedId, openRequests]);

  const mutation = useMutation({
    mutationFn: () =>
      createFn({
        data: {
          category,
          categoryLabel: categoryLabel.trim() || null,
          username: username.trim(),
          password,
          note: note.trim() || null,
          requestId: activeRequestId,
        },
      }),
    onSuccess: () => {
      toast.success("Zugangsdaten sicher übergeben. Ihr Beratungsteam wurde informiert.");
      setCategoryLabel("");
      setUsername("");
      setPassword("");
      setNote("");
      setActiveRequestId(null);
      queryClient.invalidateQueries({ queryKey: ["cockpit", "credential-handovers"] });
      queryClient.invalidateQueries({ queryKey: ["cockpit", "credential-requests"] });
      // Ticket KN6: Übergabe erledigt einen Checklisten-Punkt und setzt das
      // Inhaltskennzeichen des Bereichs – Sitzung und Startseite erneuern.
      refreshCockpitSession(queryClient);
    },
    onError: (err: any) => {
      toast.error(err?.message || "Die Übergabe konnte nicht gespeichert werden.");
    },
  });

  const rows = useMemo(() => data?.rows ?? [], [data]);


  const canSubmit =
    username.trim().length > 0 &&
    password.length > 0 &&
    (!labelRequired || categoryLabel.trim().length > 0) &&
    !mutation.isPending;

  if (isLoading) {
    return (
      <CockpitPageShell width="reading">
        <CockpitSkeletonBlock />
      </CockpitPageShell>
    );
  }

  if (denied || !data) return null;

  return (
    <CockpitPageShell width="reading">
      <header className="mb-8">
        <div className="flex items-center gap-2 text-primary">
          <KeyRound className="h-5 w-5" aria-hidden />
          <span className="font-display text-xs font-extrabold uppercase tracking-wide">
            Sichere Übergabe
          </span>
        </div>
        <h1 className="mt-3 font-display text-3xl font-extrabold text-primary md:text-4xl">
          Zugangsdaten
        </h1>
      </header>

      {openRequests.length > 0 ? (
        <section aria-labelledby="open-requests" className="mb-8 space-y-3">
          <h2
            id="open-requests"
            className="font-display text-lg font-extrabold text-primary"
          >
            Offene Anfragen Ihres Beratungsteams
          </h2>
          <ul className="space-y-3">
            {openRequests.map((req) => (
              <li
                key={req.id}
                className={
                  "rounded-lg border p-4 md:p-5 " +
                  (activeRequestId === req.id
                    ? "border-brand-button bg-brand-button/10"
                    : "border-border bg-secondary/40")
                }
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-start gap-2 text-primary">
                      <MailQuestion className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                      <span className="min-w-0 break-words font-display text-sm font-extrabold">
                        Ihr Beratungsteam bittet um:{" "}
                        {credentialDisplayName(req.category, req.categoryLabel)}
                      </span>
                    </div>
                    {req.message ? (
                      <p className="mt-2 whitespace-pre-wrap break-words font-body text-sm leading-relaxed text-muted-foreground">
                        „{req.message}“
                      </p>
                    ) : null}
                    <p className="mt-2 font-body text-xs text-muted-foreground">
                      Angefragt am {dateFmt.format(new Date(req.createdAt))}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => selectRequest(req)}
                    className="inline-flex min-h-11 shrink-0 items-center rounded-md bg-brand-button px-4 py-2 font-display text-sm font-extrabold text-brand-button-foreground transition hover:brightness-95"
                  >
                    Jetzt übergeben
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section
        aria-labelledby="handover-form"
        className="rounded-lg border border-border bg-background p-5 md:p-6"
      >
        <h2 id="handover-form" className="font-display text-lg font-extrabold text-primary">
          Zugangsdaten übergeben
        </h2>
        {activeRequestId ? (
          <div className="mt-3 flex flex-wrap items-center gap-3 rounded-md border border-brand-button bg-brand-button/10 p-3">
            <p className="min-w-0 font-body text-sm text-foreground">
              Sie beantworten gerade die Anfrage:{" "}
              <span className="font-display font-extrabold">
                {credentialDisplayName(category, categoryLabel || null)}
              </span>
            </p>
            <button
              type="button"
              onClick={() => setActiveRequestId(null)}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-md border border-border bg-background px-3 text-sm font-semibold text-foreground hover:bg-secondary"
            >
              <X className="h-4 w-4" aria-hidden /> Zuordnung aufheben
            </button>
          </div>
        ) : null}
        <form
          ref={formRef}
          className="mt-4 space-y-4"
          onSubmit={(e) => {

            e.preventDefault();
            if (canSubmit) mutation.mutate();
          }}
        >
          <div className="grid gap-4 lg:grid-cols-2">
            <label className="block">
              <span className="font-display text-xs uppercase tracking-wide text-muted-foreground">
                Kategorie
              </span>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as CredentialCategory)}
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                {CREDENTIAL_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {CREDENTIAL_CATEGORY_LABELS[c]}
                  </option>
                ))}
              </select>
            </label>

            {labelRequired ? (
              <label className="block">
                <span className="font-display text-xs uppercase tracking-wide text-muted-foreground">
                  {credentialLabelHint(category)}
                </span>
                <input
                  value={categoryLabel}
                  onChange={(e) => setCategoryLabel(e.target.value)}
                  maxLength={200}
                  required
                  className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </label>
            ) : null}
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <label className="block">
              <span className="font-display text-xs uppercase tracking-wide text-muted-foreground">
                Benutzername oder E-Mail-Adresse
              </span>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="off"
                required
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </label>
            <label className="block">
              <span className="font-display text-xs uppercase tracking-wide text-muted-foreground">
                Passwort
              </span>
              <div className="relative mt-1">
                <input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  className="w-full rounded-md border border-border bg-background px-3 py-2 pr-12 font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                  aria-pressed={showPassword}
                  className="absolute right-1 top-1/2 grid h-9 w-10 -translate-y-1/2 place-items-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" aria-hidden />
                  ) : (
                    <Eye className="h-4 w-4" aria-hidden />
                  )}
                </button>
              </div>
            </label>
          </div>

          <label className="block">
            <span className="font-display text-xs uppercase tracking-wide text-muted-foreground">
              Hinweis (optional)
            </span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="z. B. Login-URL oder Besonderheiten"
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </label>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="submit"
              disabled={!canSubmit}
              className="inline-flex min-h-11 items-center gap-2 rounded-md bg-brand-button px-4 py-2 font-display text-sm font-extrabold text-brand-button-foreground transition hover:brightness-95 disabled:opacity-60"
            >
              {mutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <ShieldCheck className="h-4 w-4" aria-hidden />
              )}
              Sicher übergeben
            </button>
            <span className="font-body text-xs text-muted-foreground">
              Verschlüsselte Speicherung · automatische Löschung nach 7 Tagen
            </span>
          </div>
        </form>
      </section>

      <section aria-labelledby="handover-list" className="mt-10">
        <h2 id="handover-list" className="font-display text-lg font-extrabold text-primary">
          Ihre Übergaben
        </h2>
        {rows.length === 0 ? (
          /* Ticket KN3: einheitlicher Leerzustand. */
          <EmptyAreaNote
            title="Hier erscheinen Ihre Übergaben"
            reason="Sie haben bisher keine Zugangsdaten übergeben, und es liegt keine Anfrage Ihres creategy-Teams vor."
            icon={<ShieldCheck className="h-8 w-8" aria-hidden />}
          />
        ) : (
          <ul className="mt-4 space-y-3">
            {rows.map((row) => (
              <li
                key={row.id}
                className="rounded-lg border border-border bg-background px-4 py-3"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="min-w-0 break-words font-display text-sm font-extrabold text-foreground">
                    {credentialDisplayName(row.category, row.categoryLabel)}
                  </span>
                  <span
                    className={
                      "rounded px-2 py-0.5 font-display text-xs " +
                      (row.status === "expired"
                        ? "bg-secondary text-muted-foreground"
                        : row.status === "viewed"
                          ? "bg-brand-wahlen/15 text-brand-wahlen"
                          : "bg-primary/10 text-primary")
                    }
                  >
                    {credentialStatusLabel(row)}
                  </span>
                </div>
                <p className="mt-1 font-body text-xs text-muted-foreground">
                  Übergeben am {dateFmt.format(new Date(row.createdAt))}
                  {row.status === "provided"
                    ? ` · verfällt am ${dateFmt.format(new Date(row.expiresAt))}`
                    : ""}
                  {row.firstViewedAt
                    ? ` · erstmals eingesehen am ${dateFmt.format(new Date(row.firstViewedAt))}`
                    : ""}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </CockpitPageShell>
  );
}
